import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { connect } from "react-redux";
import {
  fetchMySavedSearches,
  addRestaurantToList,
  removeRestaurantFromList,
  createSavedSearch,
} from "../../actions/savedSearch";

const AddToListModal = ({ restaurantId, restaurantName, onClose, token }) => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [togglingId, setTogglingId] = useState(null);

  // Inline create form
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newVisibility, setNewVisibility] = useState("private");
  const [creating, setCreating] = useState(false);

  useEffect(function loadUserListsOnMount() {
    const load = async () => {
      try {
        const data = await fetchMySavedSearches(token);
        setLists(data);
      } catch {
        setError("Failed to load your lists");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const isInList = (list) =>
    list.curatedRestaurantIds?.includes(restaurantId);

  const handleToggle = async (list) => {
    setTogglingId(list._id);
    setError("");
    try {
      if (isInList(list)) {
        const updated = await removeRestaurantFromList(
          list._id,
          restaurantId,
          token
        );
        setLists((prev) =>
          prev.map((l) => (l._id === list._id ? updated : l))
        );
      } else {
        const updated = await addRestaurantToList(
          list._id,
          restaurantId,
          token
        );
        setLists((prev) =>
          prev.map((l) => (l._id === list._id ? updated : l))
        );
      }
    } catch {
      setError("Failed to update list");
    } finally {
      setTogglingId(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    setError("");
    try {
      const created = await createSavedSearch(
        {
          name: newName.trim(),
          curatedRestaurantIds: [restaurantId],
          visibility: newVisibility,
        },
        token
      );
      setLists((prev) => [created, ...prev]);
      setNewName("");
      setShowCreate(false);
    } catch (err) {
      const errMsg =
        err.response?.data?.errors?.[0]?.msg || "Failed to create list";
      setError(errMsg);
    } finally {
      setCreating(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button
          className="modal-close"
          onClick={onClose}
          aria-label="Close modal"
        >
          &times;
        </button>

        <i
          className="fas fa-plus modal-icon"
          style={{ color: "var(--color-primary)" }}
        />
        <h3 className="modal-title">Add to List</h3>
        <p
          className="modal-message"
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {restaurantName}
        </p>

        {loading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <i className="fas fa-spinner fa-spin" />
          </div>
        ) : (
          <div style={{ maxHeight: "300px", overflowY: "auto" }}>
            {lists.map((list) => (
              <div
                key={list._id}
                onClick={() => togglingId !== list._id && handleToggle(list)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "10px 8px",
                  cursor: togglingId === list._id ? "wait" : "pointer",
                  borderBottom: "1px solid #eee",
                  opacity: togglingId === list._id ? 0.6 : 1,
                }}
              >
                <i
                  className={`fas ${
                    isInList(list) ? "fa-check-square" : "fa-square"
                  }`}
                  style={{
                    color: isInList(list) ? "var(--color-primary)" : "#ccc",
                    fontSize: "1.2em",
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {list.name}
                  </div>
                  {(list.curatedRestaurantIds?.length > 0 || list.cityName) && (
                    <div style={{ fontSize: "0.8em", color: "#888" }}>
                      {list.curatedRestaurantIds?.length > 0 &&
                        `${list.curatedRestaurantIds.length} curated`}
                      {list.curatedRestaurantIds?.length > 0 && list.cityName &&
                        " \u00B7 "}
                      {list.cityName || ""}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {error && (
          <p style={{ color: "#c62828", fontSize: "0.9em", marginTop: "8px" }}>
            {error}
          </p>
        )}

        {!showCreate ? (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            style={{
              marginTop: "12px",
              background: "none",
              border: "1px dashed #ccc",
              borderRadius: "6px",
              padding: "10px",
              width: "100%",
              cursor: "pointer",
              color: "#666",
            }}
          >
            <i className="fas fa-plus" /> Create New List
          </button>
        ) : (
          <form
            onSubmit={handleCreate}
            style={{
              marginTop: "12px",
              border: "1px solid #ddd",
              borderRadius: "6px",
              padding: "12px",
            }}
          >
            <input
              type="text"
              className="text-form"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="List name"
              autoFocus
              style={{ marginBottom: "8px", width: "100%" }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
              }}
            >
              <select
                className="text-form"
                value={newVisibility}
                onChange={(e) => setNewVisibility(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="private">Private</option>
                <option value="public">Public</option>
              </select>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                type="submit"
                className="btn-primary"
                disabled={creating || !newName.trim()}
              >
                {creating ? "Creating..." : "Create & Add"}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowCreate(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
};

const mapStateToProps = (state) => ({
  token: state.auth.token,
});

export default connect(mapStateToProps)(AddToListModal);
